import {
  getMeVisibilityGroupsTeamsOptions,
  getTeamsSearchOptions,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { useAppContext } from "@/hooks/use-app-context";

import { mapTeamDBToOption } from "../teamOption";

const formatVisibilityGroup = (
  visibilityGroup: string,
  orgNameMap: Map<string, string>,
): string => {
  if (visibilityGroup.startsWith("ORG")) {
    const orgName = orgNameMap.get(visibilityGroup);
    return orgName || "My Organization";
  } else {
    return visibilityGroup
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }
};

export const useVisibilityGroups = () => {
  const { data: visibilityGroups, isLoading: isLoadingVisibilityGroups } =
    useQuery({
      ...getMeVisibilityGroupsTeamsOptions(),
      select: (result) => result?.visibilityGroups ?? [],
    });

  const { userOrganizations } = useAppContext();

  const orgNameMap = useMemo(() => {
    const map = new Map<string, string>();
    userOrganizations.forEach((org) => {
      map.set(org.orgId, org.orgName);
    });
    return map;
  }, [userOrganizations]);

  const items = useMemo(() => {
    if (!visibilityGroups) return [];
    return visibilityGroups.map((group: string) => ({
      value: group,
      label: formatVisibilityGroup(group, orgNameMap),
    }));
  }, [visibilityGroups, orgNameMap]);

  return {
    visibilityGroupOptions: items,
    isLoadingVisibilityGroups,
  };
};

export const useTeams = ({
  searchQuery,
  visibilityGroup,
}: {
  searchQuery?: string;
  visibilityGroup?: string;
}) => {
  const { data: teamOptions = [], isLoading: isLoadingTeams } = useQuery({
    ...getTeamsSearchOptions({
      query: {
        name: searchQuery,
        visibilityGroup: visibilityGroup!,
      },
    }),
    enabled: !!visibilityGroup,
    select: (result) => {
      const mapped = result?.teams?.map(mapTeamDBToOption) ?? [];
      return mapped;
    },
  });

  return { teamOptions, isLoadingTeams };
};
